// Organik Kal cart: application coordinator.
(function initCartDrawer(global) {
    'use strict';

    const modules = global.OrganikKalCart;
    if (!modules?.api || !modules?.utils || !modules?.createCartRenderer || !modules?.createCartMarketing) {
        console.error('Organik Kal cart modules could not be loaded.');
        return;
    }

    const settings = global.LumiCartSettings || {};
    const locales = resolveLocales(settings);
    const { api, utils } = modules;
    const elements = collectElements();
    const state = {
        cart: null,
        coupon: readStorage('lumicart_coupon'),
        giftwrap: readStorage('lumicart_giftwrap') === 'true',
        giftwrapNote: readStorage('lumicart_giftwrap_note') || '',
        giftSyncing: false,
        toastTimer: null
    };

    if (!elements.drawer || !elements.overlay || !elements.items) return;

    const renderer = modules.createCartRenderer({ settings, locales, elements });
    const marketing = modules.createCartMarketing({
        settings,
        locales,
        api,
        hasCartItems: () => Boolean(state.cart?.item_count),
        onCartExpired: resetCartCampaignState,
        onExitCoupon: applyExitCoupon
    });

    function init() {
        applyThemeStyles();
        restoreFormState();
        bindEvents();
        marketing.init();
        global.LumiCartDrawer = Object.freeze({
            open: openDrawer,
            close: closeDrawer,
            refresh: refreshCart,
            notifyAdded: showAddedToast
        });
        refreshCart();
    }

    function resolveLocales(config) {
        const fallback = global.LumiCartLocales || {};
        if (config.languageOverride === 'tr' && global.LumiCartLocalesTr) return global.LumiCartLocalesTr;
        if (config.languageOverride === 'en' && global.LumiCartLocalesEn) return global.LumiCartLocalesEn;
        return fallback;
    }

    function collectElements() {
        const byId = id => document.getElementById(id);
        return {
            drawer: byId('lumicart-drawer'), overlay: byId('lumicart-overlay'),
            trigger: byId('lumicart-floating-trigger'), close: byId('lumicart-close-btn'),
            continueShopping: byId('lumicart-continue-shopping'), items: byId('lumicart-drawer-items'),
            continueShoppingFilled: byId('lumicart-continue-shopping-filled'),
            addedToast: byId('lumicart-added-toast'),
            addedToastProduct: byId('lumicart-added-toast-product'),
            addedToastAction: byId('lumicart-added-toast-action'),
            addedToastClose: byId('lumicart-added-toast-close'),
            empty: byId('lumicart-empty-state'), footer: byId('lumicart-drawer-footer'),
            badge: byId('lumicart-badge-count'), bubble: byId('lumicart-count-bubble'),
            subtotal: byId('lumicart-subtotal'), discount: byId('lumicart-discount'),
            discountLine: byId('lumicart-discount-line'), giftLine: byId('lumicart-gift-line'),
            giftName: byId('lumicart-gift-name'), shipping: byId('lumicart-shipping-fee'),
            total: byId('lumicart-total'), checkout: byId('lumicart-checkout-button'),
            giftwrapContainer: byId('lumicart-giftwrap-container'),
            giftwrapCheckbox: byId('lumicart-giftwrap-checkbox'),
            giftwrapNote: byId('lumicart-giftwrap-note'),
            giftwrapNoteWrapper: byId('lumicart-giftwrap-note-wrapper'),
            couponInput: byId('lumicart-coupon-input'), couponApply: byId('lumicart-apply-coupon-btn'),
            couponStatus: byId('lumicart-coupon-status-msg'),
            upsellContainer: byId('lumicart-upsell-container'), upsellWrapper: byId('lumicart-upsell-wrapper')
        };
    }

    function applyThemeStyles() {
        const root = document.documentElement;
        const colors = {
            '--primary-color': settings.colorPrimary,
            '--progress-color': settings.colorProgress,
            '--cart-bg': settings.colorCartBg,
            '--text-color': settings.colorText
        };
        Object.entries(colors).forEach(([property, value]) => {
            if (/^#[0-9a-f]{6}$/i.test(String(value || ''))) root.style.setProperty(property, value);
        });
        const radius = Math.min(30, Math.max(0, Number(settings.borderRadius) || 12));
        root.style.setProperty('--border-radius', `${radius}px`);
    }

    function restoreFormState() {
        if (elements.giftwrapCheckbox) elements.giftwrapCheckbox.checked = state.giftwrap;
        if (elements.giftwrapNote) elements.giftwrapNote.value = state.giftwrapNote.slice(0, 500);
        elements.giftwrapNoteWrapper?.classList.toggle('active', state.giftwrap);
        if (elements.couponInput && state.coupon) elements.couponInput.value = state.coupon;
    }

    function openDrawer() {
        hideAddedToast();
        elements.drawer.classList.add('active');
        elements.overlay.classList.add('active');
        document.documentElement.classList.add('lumicart-is-open');
        refreshCart();
    }

    function closeDrawer() {
        elements.drawer.classList.remove('active');
        elements.overlay.classList.remove('active');
        document.documentElement.classList.remove('lumicart-is-open');
    }

    function showAddedToast(product = {}) {
        if (!elements.addedToast) return;
        const title = String(product.product_title || product.title || '').trim();
        if (elements.addedToastProduct) {
            elements.addedToastProduct.textContent = title
                ? `${title} sepette seni bekliyor.`
                : 'Ürünün sepette seni bekliyor.';
        }
        positionAddedToast();
        global.clearTimeout(state.toastTimer);
        elements.addedToast.classList.remove('is-visible');
        void elements.addedToast.offsetWidth;
        elements.addedToast.classList.add('is-visible');
        elements.addedToast.setAttribute('aria-hidden', 'false');
        state.toastTimer = global.setTimeout(hideAddedToast, 5500);
    }

    function positionAddedToast() {
        if (!global.matchMedia('(max-width: 989px)').matches) return;
        const headers = document.querySelectorAll('.ok-site-header, #header-group, header-component');
        let visibleBottom = 0;

        headers.forEach(header => {
            const rect = header.getBoundingClientRect();
            const isVisible = rect.bottom > 0 && rect.top < global.innerHeight;
            if (isVisible) visibleBottom = Math.max(visibleBottom, rect.bottom);
        });

        const safeTop = Math.min(global.innerHeight * 0.32, Math.max(12, visibleBottom + 10));
        elements.addedToast.style.setProperty('--lumicart-toast-top', `${Math.round(safeTop)}px`);
    }

    function hideAddedToast() {
        global.clearTimeout(state.toastTimer);
        state.toastTimer = null;
        elements.addedToast?.classList.remove('is-visible');
        elements.addedToast?.setAttribute('aria-hidden', 'true');
    }

    async function refreshCart(expectedCount = null, attempt = 0) {
        try {
            const cart = await api.getCart();
            if (expectedCount !== null && cart.item_count !== expectedCount && attempt < 4) {
                global.setTimeout(() => refreshCart(expectedCount, attempt + 1), 300);
                return;
            }
            state.cart = cart;
            await renderCart();
        } catch (error) {
            console.error('Cart refresh failed:', error);
        }
    }

    async function renderCart() {
        const cart = state.cart;
        if (!cart) return;

        updateCartCounts(cart.item_count);
        const hasItems = cart.item_count > 0;
        toggle(elements.items, hasItems, 'flex');
        toggle(elements.empty, !hasItems, 'flex');
        toggle(elements.footer, hasItems, 'block');
        toggle(elements.giftwrapContainer, hasItems && settings.enableGiftwrap, 'block');

        if (!hasItems) {
            elements.items.replaceChildren();
            marketing.sync(cart, 0);
            return;
        }

        const giftVariantId = Number(settings.giftProductVariantId) || 0;
        const subtotal = cart.items.reduce((total, item) => {
            return isGiftItem(item, giftVariantId) ? total : total + Number(item.line_price || 0);
        }, 0) / 100;

        if (await synchronizeGift(cart, giftVariantId, subtotal)) {
            await renderCart();
            return;
        }

        renderer.renderItems(cart.items, giftVariantId);
        renderer.renderUpsells(cart.items);
        renderer.renderSummary(calculateSummary(cart, subtotal));
        marketing.sync(cart, subtotal);
    }

    function calculateSummary(cart, subtotal) {
        const shopifyDiscount = cart.items.reduce((total, item) => {
            return total + Math.max(0, Number(item.original_line_price || 0) - Number(item.line_price || 0));
        }, 0) / 100;
        const couponDiscount = calculateCouponDiscount(subtotal);
        const milestonePercent = Math.min(50, Math.max(1, Number(settings.discountPercent) || 10));
        const milestoneDiscount = subtotal >= Number(settings.discountGoal)
            ? subtotal * milestonePercent / 100 : 0;
        const giftwrapFee = settings.enableGiftwrap && state.giftwrap
            ? Number(settings.giftwrapPrice) || 45 : 0;

        return {
            subtotal,
            totalDiscount: shopifyDiscount + couponDiscount + milestoneDiscount,
            appliedDiscount: couponDiscount + milestoneDiscount,
            giftwrapFee,
            requiresShipping: cart.requires_shipping !== false,
            giftUnlocked: Number(settings.giftGoal) > 0 && subtotal >= Number(settings.giftGoal)
        };
    }

    function calculateCouponDiscount(subtotal) {
        const code = String(state.coupon || '').toUpperCase();
        const first = String(settings.couponCode1 || 'WELCOME10').trim().toUpperCase();
        const second = String(settings.couponCode2 || 'SUMMER20').trim().toUpperCase();
        const exitCode = String(settings.exitCouponCode || 'SEPET5').trim().toUpperCase();
        if (code === first) return subtotal * 0.10;
        if (code === second) return subtotal * 0.20;
        if (code === exitCode) {
            const percent = Math.min(50, Math.max(1, Number(settings.exitDiscountPercent) || 5));
            return subtotal * percent / 100;
        }
        return 0;
    }

    async function synchronizeGift(cart, giftVariantId, subtotal) {
        if (!giftVariantId || state.giftSyncing) return false;
        const giftGoal = Number(settings.giftGoal) || 0;
        const gift = cart.items.find(item => isGiftItem(item, giftVariantId));
        const shouldHaveGift = giftGoal > 0 && subtotal >= giftGoal;
        if (shouldHaveGift === Boolean(gift)) return false;

        state.giftSyncing = true;
        try {
            state.cart = shouldHaveGift
                ? await api.addItem(giftVariantId, 1, { _campaign_gift: 'true' })
                : await api.changeItem(gift.key || gift.id, 0);
            return true;
        } catch (error) {
            console.error('Campaign gift synchronization failed:', error);
            return false;
        } finally {
            state.giftSyncing = false;
        }
    }

    function isGiftItem(item, giftVariantId) {
        return item.id === giftVariantId && item.properties && item.properties._campaign_gift === 'true';
    }

    function updateCartCounts(count) {
        if (elements.badge) elements.badge.textContent = count;
        if (elements.bubble) elements.bubble.textContent = count;
        document.querySelectorAll('.ok-cart-count').forEach(element => {
            element.textContent = count;
            element.hidden = count === 0;
        });
        const selectors = ['.cart-count-bubble', '#CartCount', '[data-cart-count]', '.cart-link__count'];
        document.querySelectorAll(selectors.join(',')).forEach(element => {
            const target = element.querySelector('span') || element;
            target.textContent = count;
            element.style.display = count > 0 ? 'flex' : 'none';
        });
    }

    function bindEvents() {
        elements.trigger?.addEventListener('click', openDrawer);
        elements.close?.addEventListener('click', closeDrawer);
        elements.overlay.addEventListener('click', closeDrawer);
        elements.continueShopping?.addEventListener('click', closeDrawer);
        elements.continueShoppingFilled?.addEventListener('click', closeDrawer);
        elements.addedToastAction?.addEventListener('click', openDrawer);
        elements.addedToastClose?.addEventListener('click', hideAddedToast);
        elements.items.addEventListener('click', handleItemAction);
        elements.upsellWrapper?.addEventListener('click', handleUpsellAction);
        elements.giftwrapCheckbox?.addEventListener('change', handleGiftwrapChange);
        elements.giftwrapNote?.addEventListener('input', handleGiftwrapNote);
        elements.couponApply?.addEventListener('click', applyCouponFromInput);
        elements.checkout?.addEventListener('click', goToCheckout);
        document.querySelectorAll('[data-coupon-code]').forEach(button => {
            button.addEventListener('click', () => applyCoupon(button.dataset.couponCode));
        });
        document.addEventListener('click', interceptHeaderCart, true);
        document.addEventListener('cart:updated', handleExternalCartUpdate);
        document.addEventListener('cart:refresh', handleExternalCartUpdate);
    }

    function interceptHeaderCart(event) {
        const selector = 'a[href="/cart"], #cart-icon-bubble, .header__icon--cart, [data-cart-trigger], .cart-toggle, .header__cart-toggle';
        const trigger = event.target.closest(selector);
        if (!trigger || elements.drawer.contains(trigger)) return;
        event.preventDefault();
        event.stopPropagation();
        openDrawer();
    }

    function handleExternalCartUpdate(event) {
        if (event.detail?.source !== 'organikkal-cart') refreshCart();
    }

    async function handleItemAction(event) {
        const control = event.target.closest('[data-cart-action]');
        const item = control?.closest('.lumicart-item');
        if (!control || !item || item.classList.contains('is-updating')) return;

        let key;
        try { key = decodeURIComponent(item.dataset.cartKey || ''); }
        catch (_error) { return; }
        if (!key) return;

        const quantity = utils.positiveInteger(item.dataset.quantity);
        const action = control.dataset.cartAction;
        const nextQuantity = action === 'remove' ? 0
            : action === 'increase' ? quantity + 1 : Math.max(0, quantity - 1);
        setItemLoading(item, nextQuantity === 0);

        try {
            state.cart = await api.changeItem(key, nextQuantity);
            await renderCart();
            document.dispatchEvent(new CustomEvent('cart:updated', {
                detail: { cart: state.cart, source: 'organikkal-cart' }
            }));
        } catch (error) {
            clearItemLoading(item);
            marketing.showAlert('Sepet güncellenemedi', 'Lütfen bağlantınızı kontrol edip tekrar deneyin.');
            console.error('Cart item update failed:', error);
        }
    }

    async function handleUpsellAction(event) {
        const button = event.target.closest('[data-upsell-variant]');
        if (!button || button.disabled) return;
        const variantId = utils.positiveInteger(button.dataset.upsellVariant, 0);
        if (!variantId) return;

        button.disabled = true;
        button.textContent = settings.addingText || 'Ekleniyor...';
        try {
            const expectedCount = (state.cart?.item_count || 0) + 1;
            await api.addItem(variantId, 1);
            await refreshCart(expectedCount);
        } catch (error) {
            renderer.renderUpsells(state.cart?.items || []);
            marketing.showAlert('Ürün eklenemedi', 'Lütfen bağlantınızı kontrol edip tekrar deneyin.');
            console.error('Upsell item could not be added:', error);
        }
    }

    function setItemLoading(item, removing) {
        item.classList.add('is-updating');
        item.classList.toggle('is-removing', removing);
        item.setAttribute('aria-busy', 'true');
        item.querySelectorAll('button').forEach(button => { button.disabled = true; });
    }

    function clearItemLoading(item) {
        item.classList.remove('is-updating', 'is-removing');
        item.removeAttribute('aria-busy');
        item.querySelectorAll('button').forEach(button => { button.disabled = false; });
    }

    function handleGiftwrapChange(event) {
        state.giftwrap = Boolean(event.target.checked);
        writeStorage('lumicart_giftwrap', String(state.giftwrap));
        elements.giftwrapNoteWrapper?.classList.toggle('active', state.giftwrap);
        renderCart();
    }

    function handleGiftwrapNote(event) {
        state.giftwrapNote = String(event.target.value).slice(0, 500);
        writeStorage('lumicart_giftwrap_note', state.giftwrapNote);
    }

    function applyCouponFromInput() { applyCoupon(elements.couponInput?.value); }

    function applyCoupon(rawCode) {
        const code = String(rawCode || '').trim().toUpperCase().slice(0, 64);
        const allowed = [settings.couponCode1, settings.couponCode2, settings.exitCouponCode]
            .map(value => String(value || '').trim().toUpperCase()).filter(Boolean);

        if (!code || !allowed.includes(code)) {
            showCouponStatus(locales.coupon_invalid || 'Geçersiz kupon kodu.', false);
            return;
        }
        state.coupon = code;
        writeStorage('lumicart_coupon', code);
        if (elements.couponInput) elements.couponInput.value = code;
        const message = String(locales.coupon_applied || '{{ code }} kuponu uygulandı.').replace('{{ code }}', code);
        showCouponStatus(message, true);
        renderCart();
    }

    function applyExitCoupon(code) {
        applyCoupon(code);
        openDrawer();
    }

    function showCouponStatus(message, success) {
        if (!elements.couponStatus) return;
        elements.couponStatus.textContent = message;
        elements.couponStatus.className = `coupon-status-msg ${success ? 'success' : 'error'}`;
    }

    function goToCheckout() {
        const destination = state.coupon
            ? `/discount/${encodeURIComponent(state.coupon)}?redirect=/checkout` : '/checkout';
        global.location.assign(destination);
    }

    function resetCartCampaignState() {
        state.cart = { item_count: 0, items: [], requires_shipping: false };
        state.coupon = null;
        state.giftwrap = false;
        state.giftwrapNote = '';
        ['lumicart_coupon', 'lumicart_giftwrap', 'lumicart_giftwrap_note'].forEach(removeStorage);
        restoreFormState();
        renderCart();
    }

    function toggle(element, visible, display) {
        if (element) element.style.display = visible ? display : 'none';
    }

    function readStorage(key) {
        try { return global.localStorage.getItem(key); }
        catch (_error) { return null; }
    }

    function writeStorage(key, value) {
        try { global.localStorage.setItem(key, value); }
        catch (_error) { /* Storage can be unavailable in privacy modes. */ }
    }

    function removeStorage(key) {
        try { global.localStorage.removeItem(key); }
        catch (_error) { /* Storage can be unavailable in privacy modes. */ }
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
    else init();
})(window);
